/* eslint-disable  @typescript-eslint/no-explicit-any */
import { Util } from "Util";
import axios from "axios";
import { AdminOrder, OrderRequest } from "models/Admin";
import { AppResponse } from "models/Response";
import { User } from "models/User";
import { Driver, DriverRoutesRequest, UpdateDriverRequest } from "models/Driver";
import { Order } from "models/Order";
import { OrderStatus } from "enum/OrderStatus";
import { BillingUsers, Invoice } from "models/Invoice";
import { DriverList } from "models/DriverList";
import { AddressBook } from "models/AddressBook";
import { OrderResponse } from "models/orderResponse";
import { UserList } from "models/UserList";
import { OrderList } from "models/OrderList";
import { Setting } from "models/Setting";
import { DriverCommission, DriverCommissionList } from "models/DriverCommission";
import { QuoteModel } from "models/Quote";
import { ReportList } from "models/ReportList";
import { FinanceIdResponse, FinanceListResponse, FinanceSummaryData, UpdateFinanceData } from "models/Finance";
import { AllDispachCount } from "models/AllDispachCount";
import { BillingList } from "models/BillingList";
import { Shift, ShiftRequest } from "models/Shift";
import {
  ConnectedStore,
  ConnectedStoreAddress,
  ConnectedStoreList,
  ShopifyShopProfile,
  UpdateConnectedStoreRequest,
} from "models/ConnectedStore";
import {
  StoreDeliveryService,
  StoreDeliveryServiceList,
  StorePricingCatalog,
} from "models/StoreDeliveryService";

export class AdminService {
  private static dispatchCountCache?: AppResponse<AllDispachCount>;
  private static dispatchCountCacheTime = 0;
  private static dispatchCountRequest?: Promise<AppResponse<AllDispachCount>>;
  private static readonly dispatchCountCacheDuration = 15000;

  public static invalidateDispatchCountCache(): void {
    AdminService.dispatchCountCache = undefined;
    AdminService.dispatchCountCacheTime = 0;
  }

  /* CUSTOMERS */
  public static async getConnectedShopifyStores(
    limit: number,
    offset: number,
    search?: string
  ): Promise<AppResponse<ConnectedStoreList>> {
    const ep = Util.apiAuthUrl(
      `admin/store/shopify/list/${offset}/${limit}`
    );
    const trimmedSearch = (search || "").trim();
    return axios.get<void, AppResponse<ConnectedStoreList>>(ep, {
      params: trimmedSearch ? { search: trimmedSearch } : undefined,
    });
  }

  public static async createCustomStoreInvite(payload: {
    storeName?: string;
    emailLock?: string;
    expiresInHours?: number;
    /** When set, creates a one-time access key locked to this Zulu customerId. */
    customerId?: string;
  }): Promise<
    AppResponse<{
      inviteId: string;
      tokenPrefix: string;
      storeNameHint: string | null;
      emailLock: string | null;
      customerIdLock: string | null;
      customer: {
        id: string;
        customerId: string;
        firstName: string;
        lastName: string;
        email: string;
      } | null;
      expiresAt: string;
      expiresInHours: number;
      invitePath: string | null;
      inviteUrl: string | null;
      inviteToken: string;
      accessKey: string | null;
    }>
  > {
    const ep = Util.apiAuthUrl("admin/store/custom/invite");
    return axios.post(ep, payload);
  }

  public static async revokeCustomStoreInvite(
    inviteId: string
  ): Promise<AppResponse<{ inviteId: string; status: string }>> {
    const ep = Util.apiAuthUrl(`admin/store/custom/invite/${inviteId}/revoke`);
    return axios.post(ep, {});
  }

  public static async lookupCustomStoreCustomerByZuluId(
    customerId: string
  ): Promise<
    AppResponse<{
      customer: {
        id: string;
        customerId: string;
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        verifiedStatus: string;
        role: string;
        address: ConnectedStoreAddress | null;
      };
      businessName: string | null;
      suggestedStoreName: string;
    }>
  > {
    const ep = Util.apiAuthUrl(
      `admin/store/custom/customer/${encodeURIComponent(customerId.trim())}`
    );
    return axios.get(ep);
  }

  public static async searchCustomStoreCustomers(
    query: string
  ): Promise<
    AppResponse<{
      results: Array<{
        customer: {
          id: string;
          customerId: string;
          firstName: string;
          lastName: string;
          email: string;
          phoneNumber: string;
          verifiedStatus: string;
          role: string;
          address: ConnectedStoreAddress | null;
        };
        businessName: string | null;
        suggestedStoreName: string;
      }>;
    }>
  > {
    const ep = Util.apiAuthUrl("admin/store/custom/customer-search");
    return axios.get(ep, { params: { q: query.trim() } });
  }

  public static async updateConnectedShopifyStore(
    integrationId: string,
    store: UpdateConnectedStoreRequest
  ): Promise<AppResponse<unknown>> {
    const ep = Util.apiAuthUrl(`admin/store/shopify/${integrationId}`);
    return axios.put<UpdateConnectedStoreRequest, AppResponse<unknown>>(
      ep,
      store
    );
  }

  public static async getConnectedShopifyStore(
    integrationId: string
  ): Promise<AppResponse<ConnectedStore>> {
    const ep = Util.apiAuthUrl(`admin/store/shopify/${integrationId}`);
    return axios.get<void, AppResponse<ConnectedStore>>(ep);
  }

  public static async getConnectedShopProfile(
    integrationId: string
  ): Promise<AppResponse<ShopifyShopProfile>> {
    const ep = Util.apiAuthUrl(
      `admin/store/shopify/${integrationId}/shop-profile`
    );
    return axios.get<void, AppResponse<ShopifyShopProfile>>(ep);
  }

  public static async getStoreDeliveryServices(
    integrationId: string
  ): Promise<AppResponse<StoreDeliveryServiceList>> {
    const ep = Util.apiAuthUrl(
      `admin/store/shopify/${integrationId}/services`
    );
    return axios.get<void, AppResponse<StoreDeliveryServiceList>>(ep);
  }

  public static async getStoreDeliveryService(
    integrationId: string,
    serviceId: string
  ): Promise<AppResponse<StoreDeliveryService>> {
    const ep = Util.apiAuthUrl(
      `admin/store/shopify/${integrationId}/services/${serviceId}`
    );
    return axios.get<void, AppResponse<StoreDeliveryService>>(ep);
  }

  public static async createStoreDeliveryService(
    integrationId: string,
    service: StoreDeliveryService
  ): Promise<AppResponse<StoreDeliveryService>> {
    const ep = Util.apiAuthUrl(
      `admin/store/shopify/${integrationId}/services`
    );
    return axios.post<StoreDeliveryService, AppResponse<StoreDeliveryService>>(
      ep,
      service
    );
  }

  public static async updateStoreDeliveryService(
    integrationId: string,
    serviceId: string,
    service: StoreDeliveryService
  ): Promise<AppResponse<StoreDeliveryService>> {
    const ep = Util.apiAuthUrl(
      `admin/store/shopify/${integrationId}/services/${serviceId}`
    );
    return axios.put<StoreDeliveryService, AppResponse<StoreDeliveryService>>(
      ep,
      service
    );
  }

  public static async activateStoreDeliveryService(
    integrationId: string,
    serviceId: string
  ): Promise<AppResponse<StoreDeliveryService>> {
    const ep = Util.apiAuthUrl(
      `admin/store/shopify/${integrationId}/services/${serviceId}/activate`
    );
    return axios.post<void, AppResponse<StoreDeliveryService>>(ep);
  }

  public static async deactivateStoreDeliveryService(
    integrationId: string,
    serviceId: string
  ): Promise<AppResponse<StoreDeliveryService>> {
    const ep = Util.apiAuthUrl(
      `admin/store/shopify/${integrationId}/services/${serviceId}/deactivate`
    );
    return axios.post<void, AppResponse<StoreDeliveryService>>(ep);
  }

  public static async getStorePricingCatalog(
    integrationId: string
  ): Promise<AppResponse<StorePricingCatalog>> {
    const ep = Util.apiAuthUrl(
      `admin/store/shopify/${integrationId}/pricing-catalog`
    );
    return axios.get<void, AppResponse<StorePricingCatalog>>(ep);
  }

  public static async toggleStoreDefaultService(
    integrationId: string,
    settingsType: string,
    enabled: boolean
  ): Promise<AppResponse<StorePricingCatalog>> {
    const ep = Util.apiAuthUrl(
      `admin/store/shopify/${integrationId}/default-services`
    );
    return axios.patch<
      { settingsType: string; enabled: boolean },
      AppResponse<StorePricingCatalog>
    >(ep, { settingsType, enabled });
  }

  public static async updateStoreDefaultService(
    integrationId: string,
    settingsType: string,
    data: {
      name: string;
      description: string;
      cutoffTime: string;
      deliveryWindowStart: string;
      deliveryWindowEnd: string;
    }
  ): Promise<AppResponse<StorePricingCatalog>> {
    const ep = Util.apiAuthUrl(
      `admin/store/shopify/${integrationId}/default-services/${settingsType}`
    );
    return axios.put<typeof data, AppResponse<StorePricingCatalog>>(ep, data);
  }

  public static async getCustomerList(limit?: number, offset?: number): Promise<AppResponse<UserList>> {
    const ep = Util.apiAuthUrl(`admin/user/list/` + offset + "/" + limit);
    const res = await axios.get<void, AppResponse<UserList>>(ep);
    return res;
  }

  public static async getPersonalCustomerList(limit?: number, offset?: number): Promise<AppResponse<UserList>> {
    const ep = Util.apiAuthUrl(`admin/personal/list/` + offset + "/" + limit);
    const res = await axios.get<void, AppResponse<UserList>>(ep);
    return res;
  }

  public static async getBusinessCustomerList(limit?: number, offset?: number): Promise<AppResponse<UserList>> {
    const ep = Util.apiAuthUrl(`admin/business/list/` + offset + "/" + limit);
    const res = await axios.get<void, AppResponse<UserList>>(ep);
    return res;
  }
  public static async getMonthlyCustomerList(limit?: number, offset?: number): Promise<AppResponse<UserList>> {
    const ep = Util.apiAuthUrl(`admin/monthly/list/` + offset + "/" + limit);
    const res = await axios.get<void, AppResponse<UserList>>(ep);
    return res;
  }
  public static async getDeactiveCustomerList(limit?: number, offset?: number): Promise<AppResponse<UserList>> {
    const ep = Util.apiAuthUrl(`admin/deactive/list/` + offset + "/" + limit);
    const res = await axios.get<void, AppResponse<UserList>>(ep);
    return res;
  }

  public static async getMobileUsersList(limit?: number, offset?: number): Promise<AppResponse<UserList>> {
    const ep = Util.apiAuthUrl(`admin/mobileUsers/list/` + offset + "/" + limit);
    const res = await axios.get<void, AppResponse<UserList>>(ep);
    return res;
  }

  public static async getCustomerById(userId?: string): Promise<AppResponse<User>> {
    const ep = Util.apiAuthUrl(`user/` + userId);
    const res = await axios.get<void, AppResponse<User>>(ep);
    return res;
  }

  public static async createCustomer(customer: User): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/user/create");
    const res = await axios.post<User, AppResponse<string>>(ep, customer);
    return res;
  }

  public static async updateCustomer(customer: User): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/user/update");
    const res = await axios.post<User, AppResponse<string>>(ep, customer);
    return res;
  }

  public static async updateCustomerData(customer: User): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/user/update-data");
    const res = await axios.post<User, AppResponse<string>>(ep, customer);
    return res;
  }
  public static async updateCustomerActiveDeactive(customer: User): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/user/active-deactive");
    const res = await axios.post<User, AppResponse<string>>(ep, customer);
    return res;
  }
  public static async updateCustomerNotificationStatus(customer: User): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/user/delivery-notification");
    const res = await axios.post<User, AppResponse<string>>(ep, customer);
    return res;
  }
  public static async updateCustomerAutoPaymentStatus(customer: User): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("auto-payment-invoice-update");
    const res = await axios.post<User, AppResponse<string>>(ep, customer);
    return res;
  }

  public static async updateBusinessCustomer(customer: User): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/user/operating-hours");
    const res = await axios.post<User, AppResponse<string>>(ep, customer);
    return res;
  }

  /* ORDERS */
  public static async createOrder(order: AdminOrder): Promise<AppResponse<OrderResponse>> {
    const ep = Util.apiAuthUrl("admin/order/create");
    const traceId = Util.createClientTraceId("web-admin-order");
    const startedAt = Date.now();
    const res = await axios.post<AdminOrder, AppResponse<OrderResponse>>(ep, order, {
      headers: { "X-Client-Trace-Id": traceId },
    });
    Util.logOrderSubmitTiming({
      endpoint: "POST /api/auth/admin/order/create",
      traceId,
      elapsedMs: Date.now() - startedAt,
      success: !!res?.success,
      role: "admin",
      error: res?.error,
    });
    AdminService.invalidateDispatchCountCache();
    return res;
  }

  public static async orderDetails(order: AdminOrder): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/order/details");
    const res = await axios.post<AdminOrder, AppResponse<string>>(ep, order);
    return res;
  }

  public static async getOrdersByStatus(data: any, status: OrderStatus, limit?: number, offset?: number): Promise<AppResponse<OrderList>> {
    const ep = Util.apiAuthUrl(`admin/order/list/` + status + "/" + offset + "/" + limit);
    return await axios.post<void, AppResponse<OrderList>>(ep, data);
  }
  public static async getAllTrackingOrders(): Promise<any> {
    const ep = Util.apiAuthUrl("admin/order/list/getLiveOrder");
    const res = await axios.get<void, AppResponse<OrderList>>(ep);
    return res;
  }

  public static async getDriversByCustomerIds(customerIds: string[]): Promise<any> {
    const ep = Util.apiAuthUrl('admin/live/drivers');      // ← matches the route below
    const res = await axios.post<{ customerIds: string[] }, AppResponse<[]>>(
      ep,
      { customerIds }
    );
    return res;
  }

  // public static async getOrderdispachsBySearch(data: any, limit?: number, offset?: number): Promise<AppResponse<Order[]>> {
  //   const ep = Util.apiAuthUrl(`admin/dispachlistbysearch/` + offset + "/" + limit);
  //   return await axios.post<void, AppResponse<Order[]>>(ep, data);
  // }

  public static async getOrderdispachsBySearch(data: any, limit?: number, offset?: number): Promise<AppResponse<OrderList>> {
    const ep = Util.apiAuthUrl(`admin/dispachlistbysearch/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<OrderList>>(ep, data);
  }

  public static async getUsersBySearch(data: any, limit?: number, offset?: number): Promise<AppResponse<User[]>> {
    const ep = Util.apiAuthUrl(`admin/getUserBySearch/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<User[]>>(ep, data);
  }

  public static async getDriverBySearch(data: any, limit?: number, offset?: number): Promise<AppResponse<User[]>> {
    const ep = Util.apiAuthUrl(`admin/getDriverBySearch/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<User[]>>(ep, data);
  }

  public static async getPendingDriverBySearch(data: any, limit?: number, offset?: number): Promise<AppResponse<User[]>> {
    const ep = Util.apiAuthUrl(`admin/getPendingDriverBySearch/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<User[]>>(ep, data);
  }

  public static async getOrderById(orderId?: string): Promise<AppResponse<OrderResponse>> {
    const ep = Util.apiAuthUrl(`admin/order/` + orderId);
    const res = await axios.get<void, AppResponse<OrderResponse>>(ep);
    return res;
  }

  public static async getOrderByZuluId(orderId?: string): Promise<AppResponse<OrderResponse>> {
    const ep = Util.apiAuthUrl(`admin/zuluorder/` + orderId);
    const res = await axios.get<void, AppResponse<OrderResponse>>(ep);
    return res;
  }

  public static async searchOrders(uniqueId: string, limit?: number, offset?: number): Promise<AppResponse<AdminOrder[]>> {
    const ep = Util.apiAuthUrl(`admin/order/search/` + uniqueId + "/" + offset + "/" + limit);
    return await axios.get<void, AppResponse<AdminOrder[]>>(ep);
  }

  public static async updateOrderValues(uniqueId: string, data: OrderRequest): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/order/update/" + uniqueId);
    const res = await axios.post<OrderRequest, AppResponse<string>>(ep, data);
    return res;
  }

  public static async updateOrderStatus(data: OrderRequest): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/order/status");
    const res = await axios.post<OrderRequest, AppResponse<any>>(ep, data, { headers: { "Content-Type": "multipart/form-data" } });
    AdminService.invalidateDispatchCountCache();
    return res;
  }

  public static async updateMultipleOrderStatus(data: OrderRequest): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/order/multipleStatus");
    const res = await axios.post<OrderRequest, AppResponse<any>>(ep, data, { headers: { "Content-Type": "multipart/form-data" } });
    AdminService.invalidateDispatchCountCache();
    return res;
  }

  public static async cancelOrder(data: OrderRequest): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/order/status/cancel");
    const res = await axios.post<OrderRequest, AppResponse<any>>(ep, data);
    AdminService.invalidateDispatchCountCache();
    return res;
  }

  /* DRIVERS */
  public static async getDrivers(limit?: number, offset?: number): Promise<AppResponse<DriverList>> {
    const url = Util.apiAuthUrl(`admin/driver/list/` + offset + "/" + limit);
    return await axios.get<void, AppResponse<DriverList>>(url);
  }
  public static async getDriversSearch(data?: any, limit?: number, offset?: number): Promise<AppResponse<DriverList>> {
    const ep = Util.apiAuthUrl(`admin/driver/search/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<DriverList>>(ep, data);
  }

  public static async updateDriver(data?: any): Promise<AppResponse<Driver>> {
    const ep = Util.apiAuthUrl(`admin/driver/edit`);
    return await axios.post<void, AppResponse<Driver>>(ep, data);
  }

  public static async getPendingDrivers(limit?: number, offset?: number): Promise<AppResponse<DriverList>> {
    const url = Util.apiAuthUrl(`admin/driver-pending/list/` + offset + "/" + limit);
    return await axios.get<void, AppResponse<DriverList>>(url);
  }
  public static async getPendingDriversSearch(data?: any, limit?: number, offset?: number): Promise<AppResponse<DriverList>> {
    const ep = Util.apiAuthUrl(`admin/driver-pending/search/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<DriverList>>(ep, data);
  }

  public static async getDispatch(limit?: number, offset?: number): Promise<AppResponse<UserList>> {
    const url = Util.apiAuthUrl(`admin/dispatch/list/` + offset + "/" + limit);
    return await axios.get<void, AppResponse<UserList>>(url);
  }

  public static async getDispatchSearch(data?: any, limit?: number, offset?: number): Promise<AppResponse<User[]>> {
    const ep = Util.apiAuthUrl(`admin/dispatch/search/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<User[]>>(ep, data);
  }

  public static async postDriverByOrder(data: UpdateDriverRequest): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/order/driver");
    const res = await axios.post<UpdateDriverRequest, AppResponse<string>>(ep, data);
    AdminService.invalidateDispatchCountCache();
    return res;
  }
  public static async pushDriverByOrder(data: UpdateDriverRequest): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/order/push/driver");
    const res = await axios.post<UpdateDriverRequest, AppResponse<string>>(ep, data);
    AdminService.invalidateDispatchCountCache();
    return res;
  }

  public static async getDriverById(userId: string): Promise<AppResponse<Driver>> {
    const ep = Util.apiAuthUrl(`admin/driver/` + userId);
    const res = await axios.get<void, AppResponse<Driver>>(ep);
    return res;
  }

  public static async createDriver(driver: Driver): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/driver/create");
    const res = await axios.post<Driver, AppResponse<string>>(ep, driver);
    return res;
  }

  public static async updateDriverData(data?: any): Promise<AppResponse<Driver>> {
    const ep = Util.apiAuthUrl("admin/updateDriver");
    const res = await axios.post<Driver, AppResponse<Driver>>(ep, data);
    return res;
  }

  public static async deleteDriverById(driverId: any): Promise<AppResponse<Driver>> {
    const ep = Util.apiAuthUrl(`admin/driver/delete/${driverId}`);
    const res = await axios.delete<void, AppResponse<Driver>>(ep);
    return res;
  }

  public static async deleteDispatchById(dispatchId: any): Promise<AppResponse<Driver>> {
    const ep = Util.apiAuthUrl(`admin/dispatch/delete/${dispatchId}`);
    const res = await axios.delete<void, AppResponse<Driver>>(ep);
    return res;
  }

  /* FINANCE */
  public static async getCustomerBilling(data: any): Promise<AppResponse<Invoice[]>> {
    const ep = Util.apiAuthUrl("admin/billing/customer-list");
    const res = await axios.post<void, AppResponse<Invoice[]>>(ep, data);
    return res;
  }

  public static async getCustomerBillingBySearch(data: any): Promise<AppResponse<Invoice[]>> {
    const ep = Util.apiAuthUrl("admin/billing/search/customer-list");
    return await axios.post<void, AppResponse<Invoice[]>>(ep, data);
  }

  // public static async getBusinessCustomerBilling(data: any): Promise<AppResponse<Invoice[]>> {
  //   const ep = Util.apiAuthUrl("admin/billing/business-customer-list");
  //   const res = await axios.post<void, AppResponse<Invoice[]>>(ep, data);
  //   return res;
  // }

  public static async getBusinessCustomerBilling(data: any, limit?: number, offset?: number): Promise<AppResponse<BillingList>> {
    const ep = Util.apiAuthUrl("admin/billing/business-customer-list/" + offset + "/" + limit);
    const res = await axios.post<void, AppResponse<BillingList>>(ep, data);

    return res;
  }

  public static async getBusinessCustomerInvoices(data: any, limit?: number, offset?: number): Promise<AppResponse<BillingUsers>> {
    const ep = Util.apiAuthUrl("admin/billing/business-customer-invoices/" + offset + "/" + limit);
    const res = await axios.post<void, AppResponse<BillingUsers>>(ep, data);
    return res;
  }

  public static async getBusinessCustomerInvoicesBySearch(data: any): Promise<AppResponse<BillingUsers>> {
    const ep = Util.apiAuthUrl("admin/billing/search/business-customer-invoices");
    const res = await axios.post<void, AppResponse<BillingUsers>>(ep, data);
    return res;
  }

  public static async sendBusinessCustomerInvoices(data: any): Promise<AppResponse<Invoice[]>> {
    const ep = Util.apiAuthUrl("admin/billing/send-business-Customer-invoices");
    const res = await axios.post<void, AppResponse<Invoice[]>>(ep, data);
    return res;
  }

  public static async getInvoiceById(invoiceId: string): Promise<AppResponse<Invoice>> {
    const ep = Util.apiAuthUrl(`admin/billing/` + invoiceId);
    const res = await axios.get<void, AppResponse<Invoice>>(ep);
    return res;
  }

  public static async genarateInvoicePDF(invoiceId?: string): Promise<AppResponse<ArrayBuffer>> {
    const ep = Util.apiAuthUrl(`admin/billing/pdf/` + invoiceId);
    const res = await axios.get<Invoice, AppResponse<ArrayBuffer>>(ep, {
      responseType: "arraybuffer", // Specify responseType as arraybuffer
    });
    return res;
  }

  // public static async getLatestDriverBilling(data: any, limit?: number, offset?: number): Promise<AppResponse<DriverCommissionList>> {
  //   const ep = Util.apiAuthUrl("admin/billing/latest-driver-list"+ "/" + offset + "/" + limit);
  //   const res = await axios.post<void, AppResponse<DriverCommissionList>>(ep, data);
  //   return res;
  // }
  // In AdminService.ts
  public static async getLatestDriverBilling(): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/drivers/current-week-summary");
    const res = await axios.get<void, AppResponse<any>>(ep);
    return res;
  }

  public static async getDriverBilling(data: any, limit?: number, offset?: number): Promise<AppResponse<DriverCommissionList>> {
    const ep = Util.apiAuthUrl("admin/billing/driver-list" + "/" + offset + "/" + limit);
    const res = await axios.post<void, AppResponse<DriverCommissionList>>(ep, data);
    return res;
  }

  public static async getAllDriverBilling(data: any, limit?: number, offset?: number): Promise<AppResponse<DriverCommissionList>> {
    const ep = Util.apiAuthUrl("admin/billing/all-driver-list" + "/" + offset + "/" + limit);
    const res = await axios.post<void, AppResponse<DriverCommissionList>>(ep, data);
    return res;
  }

  public static async getAllDriverOldBillingReport(data: any, limit?: number, offset?: number): Promise<AppResponse<DriverCommissionList>> {
    const ep = Util.apiAuthUrl("admin/billing/old-driver-billing-report-list" + "/" + offset + "/" + limit);
    const res = await axios.post<void, AppResponse<DriverCommissionList>>(ep, data);
    return res;
  }
  public static async getAllDriverOldBilling(data: any, limit?: number, offset?: number): Promise<AppResponse<DriverCommissionList>> {
    const ep = Util.apiAuthUrl("admin/billing/old-driver-billing-list" + "/" + offset + "/" + limit);
    const res = await axios.post<void, AppResponse<DriverCommissionList>>(ep, data);
    return res;
  }

  // public static async getAllDriverOldBillingSummary(): Promise<AppResponse<any>> {
  //   const ep = Util.apiAuthUrl("admin/drivers/old-week-summary");
  //   const res = await axios.get<void, AppResponse<any>>(ep);
  //   return res;
  // }

  public static async getOldWeekDriversSummary(year?: number, week?: number): Promise<AppResponse<any>> {
    let ep = Util.apiAuthUrl("admin/drivers/old-week-summary");

    if (year && week) {
      ep += `?year=${year}&week=${week}`;
    }

    const res = await axios.get<void, AppResponse<any>>(ep);
    return res;
  }

  public static async generateDriverBillPDF(data: any): Promise<AppResponse<ArrayBuffer>> {
    const ep = Util.apiAuthUrl("admin/driver-billing/pdf");
    const res = await axios.post<DriverCommission, AppResponse<ArrayBuffer>>(ep, data, {
      responseType: "arraybuffer", // Specify responseType as arraybuffer
    });
    return res;
  }
  public static async generateDriverReportPDF(data: any): Promise<AppResponse<ArrayBuffer>> {
    const ep = Util.apiAuthUrl("admin/driver-report/pdf");
    const res = await axios.post<DriverCommission, AppResponse<ArrayBuffer>>(ep, data, {
      responseType: "arraybuffer",
    });
    return res;
  }

  public static async sendDriverBillingInvoices(data: any): Promise<AppResponse<DriverCommission[]>> {
    const ep = Util.apiAuthUrl("admin/billing/send-driver-invoices");
    const res = await axios.post<void, AppResponse<DriverCommission[]>>(ep, data);
    return res;
  }

  public static async getOldDriverBilling(driverId?: string, limit?: number, offset?: number): Promise<AppResponse<DriverCommissionList>> {
    const url = Util.apiAuthUrl(`admin/billing/driver-old-list/` + driverId + "/" + offset + "/" + limit);
    return await axios.get<void, AppResponse<DriverCommissionList>>(url);
  }

  public static async getOldDriverBillingBySearch(data: any, limit?: number, offset?: number): Promise<AppResponse<DriverCommission[]>> {
    const ep = Util.apiAuthUrl(`admin/billing/old-search-list/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<DriverCommission[]>>(ep, data);
  }

  public static async getDriverBillingBySearch(data: any, limit?: number, offset?: number): Promise<AppResponse<DriverCommission[]>> {
    const ep = Util.apiAuthUrl(`admin/billing/search-list/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<DriverCommission[]>>(ep, data);
  }

  public static async generateOldDriverBillsPDF(driverId?: string): Promise<AppResponse<ArrayBuffer>> {
    const ep = Util.apiAuthUrl(`admin/driver-old-billings/pdf/` + driverId);
    const res = await axios.post<DriverCommission, AppResponse<ArrayBuffer>>(ep, {
      responseType: "arraybuffer", // Specify responseType as arraybuffer
    });
    return res;
  }

  /* ADDRESS BOOKS */
  public static async getCustomerAddressBooks(data: any): Promise<AppResponse<AddressBook[]>> {
    const ep = Util.apiAuthUrl("admin/address-book/list");
    const res = await axios.post<void, AppResponse<AddressBook[]>>(ep, data);
    return res;
  }

  /* SETTINGS */
  public static async getSettings(): Promise<AppResponse<Setting[]>> {
    const ep = Util.apiAuthUrl("admin/setting/list");
    const res = await axios.get<void, AppResponse<Setting[]>>(ep);
    return res;
  }

  public static async updateSettings(data: Setting): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/setting/edit");
    const res = await axios.post<Setting, AppResponse<string>>(ep, data);
    return res;
  }

  /* Reports */
  public static async getOrderReport(data: any): Promise<AppResponse<ReportList>> {
    const ep = Util.apiAuthUrl("admin/report/order-report");
    const res = await axios.post<void, AppResponse<ReportList>>(ep, data);
    return res;
  }
  public static async getCustomerReport(data: any, limit?: number, offset?: number): Promise<AppResponse<ReportList>> {
    const ep = Util.apiAuthUrl(`admin/report/customer-report/${offset}/${limit}`);
    const res = await axios.post<void, AppResponse<ReportList>>(ep, data);
    return res;
  }

  public static async getCustomerReportBySearch(
    data: any,
    limit: number = 10,
    offset: number = 1
  ): Promise<AppResponse<ReportList>> {
    const url = Util.apiAuthUrl(`admin/report/customer-report-search/${offset}/${limit}`);
    const res = await axios.post<void, AppResponse<ReportList>>(url, data);
    return res;
  }

  public static async searchCustomersByName(searchText: string, offset: number = 0, limit: number = 10):
    Promise<AppResponse<{ _id: string; firstName: string; lastName: string }[]>> { // Updated type

    const url = Util.apiAuthUrl("admin/customers/monthly/search");

    const res = await axios.get<void, AppResponse<{ _id: string; firstName: string; lastName: string }[]>>(url, {
      params: { key: searchText, offset, limit },
    });

    return res;
  }


  /* QUOTE - PUBLIC ROUTE */
  public static async generateQuote(data: any): Promise<AppResponse<QuoteModel>> {
    const ep = Util.apiPublicUrl("quote/create");
    const res = await axios.post<void, AppResponse<QuoteModel>>(ep, data);
    return res;
  }

  /*finance  */

  public static async getAllFinance(limit?: number, offset?: number): Promise<AppResponse<FinanceListResponse>> {
    const url = Util.apiAuthUrl(`admin/finance/list/${offset}/${limit}`);
    return await axios.get<void, AppResponse<FinanceListResponse>>(url);
  }

  public static async getFinanceBySearch(data?: any, limit?: number, offset?: number): Promise<AppResponse<FinanceListResponse>> {
    const ep = Util.apiAuthUrl(`admin/Financelistbysearch/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<FinanceListResponse>>(ep, data);
  }

  public static async getFinanceByDate(data?: any, limit?: number, offset?: number): Promise<AppResponse<FinanceListResponse>> {
    const ep = Util.apiAuthUrl(`admin/FinancelistbyDate/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<FinanceListResponse>>(ep, data);
  }

  public static async getFinanceModal(userId?: string, limit?: number, offset?: number): Promise<AppResponse<FinanceIdResponse>> {
    const ep = Util.apiAuthUrl(`admin/FinanceModal/${userId}/${offset}/${limit}`);
    return await axios.post<void, AppResponse<FinanceIdResponse>>(ep);
  }

  public static async getFinanceModalBySearch(
    data?: any,
    userId?: string,
    limit?: number,
    offset?: number
  ): Promise<AppResponse<FinanceIdResponse>> {
    const ep = Util.apiAuthUrl(`admin/FinanceModalbysearch/${userId}/${offset}/${limit}`);
    return await axios.post<void, AppResponse<FinanceIdResponse>>(ep, data);
  }

  public static async getFinanceModalByDate(
    data?: any,
    userId?: string,
    limit?: number,
    offset?: number
  ): Promise<AppResponse<FinanceIdResponse>> {
    const ep = Util.apiAuthUrl(`admin/FinanceModalbyDate/${userId}/${offset}/${limit}`);
    return await axios.post<void, AppResponse<FinanceIdResponse>>(ep, data);
  }

  public static async getalldispachcount(): Promise<AppResponse<AllDispachCount>> {
    const now = Date.now();
    if (
      AdminService.dispatchCountCache &&
      now - AdminService.dispatchCountCacheTime < AdminService.dispatchCountCacheDuration
    ) {
      return AdminService.dispatchCountCache;
    }

    if (AdminService.dispatchCountRequest) {
      return AdminService.dispatchCountRequest;
    }

    const url = Util.apiAuthUrl(`admin/order/getalldispachcount`);
    AdminService.dispatchCountRequest = axios
      .post<void, AppResponse<AllDispachCount>>(url)
      .then(res => {
        if (res.success) {
          AdminService.dispatchCountCache = res;
          AdminService.dispatchCountCacheTime = Date.now();
        }
        return res;
      })
      .finally(() => {
        AdminService.dispatchCountRequest = undefined;
      });

    return AdminService.dispatchCountRequest;
  }

  public static async sendInvoiceEmail(invoiceId?: string, data?: any): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/send-invoice-email/pdf/` + invoiceId);
    const res = await axios.post<void, AppResponse<any>>(ep, data);
    return res;
  }

  public static async sendSelectedPaymentReminderEmail(customerIds?: any): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/send-selected-payment-reminder-email/pdf/`);
    const res = await axios.post<void, AppResponse<any>>(ep, customerIds);
    return res;
  }

  public static async sendSelectedInvoicesEmail(data?: any): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/send-selected-invoices-email/pdf/`);
    const res = await axios.post<void, AppResponse<any>>(ep, data);
    return res;
  }

  public static async DispatchUserSignup(userSignupData: User): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/user/create");
    return await axios.post<User, AppResponse<string>>(ep, userSignupData);
  }

  public static async getFinanceSummary(): Promise<AppResponse<FinanceSummaryData>> {
    const url = Util.apiAuthUrl(`admin/finance/summary`);
    const response = await axios.get<AppResponse<FinanceSummaryData>>(url);
    return response.data;
  }

  public static async getFinancePastInvoice(): Promise<AppResponse<FinanceSummaryData>> {
    const url = Util.apiAuthUrl(`admin/finance/summary`);
    const response = await axios.get<AppResponse<FinanceSummaryData>>(url);
    return response.data;
  }

  public static async genarateFianancePDF(invoiceId: string): Promise<AppResponse<ArrayBuffer>> {
    const ep = Util.apiAuthUrl(`finance/pdf/${invoiceId}`);
    const res = await axios.get<Order, AppResponse<ArrayBuffer>>(ep, {
      responseType: "arraybuffer", // Specify responseType as arraybuffer
    });
    return res;
  }

  public static async sendDriverInvoiceEmail(data: any): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/send-driver-invoice-email/pdf");
    const res = await axios.post<void, AppResponse<any>>(ep, data);
    return res;
  }

  public static async sendDriverReportEmail(data: any): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/send-driver-report-email/pdf");
    const res = await axios.post<void, AppResponse<any>>(ep, data);
    return res;
  }

  public static async updatePaymentDetails(data: UpdateFinanceData): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/finance/edit");
    const res = await axios.post<void, AppResponse<any>>(ep, data);
    return res;
  }

  // public static async postEdit(
  //   editInvoiceId?: string,
  //   editNewAmount?: number,
  //   editNewDiscount?: number,
  //   editNewPayment?: string,
  //   editNewStatus?: string
  // ): Promise<AppResponse<FinanceIdResponse>> {
  //   const ep = Util.apiAuthUrl("admin/FinancePostEdit"); // Assuming the endpoint is correct
  //   const requestData = {
  //     editInvoiceId,
  //     editNewAmount,
  //     editNewDiscount,
  //     editNewPayment,
  //     editNewStatus,
  //   };

  //   return await axios.post<void, AppResponse<FinanceIdResponse>>(ep, requestData);
  // }

  public static async getPendingSearchUsers(data?: any, limit?: number, offset?: number): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/getPendingUserBySearch/` + offset + "/" + limit);
    return await axios.post<void, AppResponse<any>>(ep, data);
  }

  public static async updateUserStatus(customer: User): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/updateUserStatus");
    const res = await axios.post<User, AppResponse<string>>(ep, customer);
    return res;
  }

  public static async updateDriverStatus(driver: Driver): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/updateDriverStatus");
    const res = await axios.post<Driver, AppResponse<string>>(ep, driver);
    return res;
  }

  public static async updateDriverPassword(data?: any): Promise<AppResponse<Driver>> {
    const ep = Util.apiAuthUrl("admin/driver/updatePassword");
    const res = await axios.post<Driver, AppResponse<Driver>>(ep, data);
    return res;
  }


  // broadcats
  public static async createBroadCastMessage(data?: any): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/broadcastMessageCreate");
    const res = await axios.post<any, AppResponse<any>>(ep, data);
    return res;
  }

  public static async deleteBroadCastMessage(id: string): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/broadcastDelete/${id}`);
    const res = await axios.delete<any, AppResponse<any>>(ep);
    return res;
  }

  public static async updateBroadcastStatus(id: string, status: "Active" | "Inactive"): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/broadcastUpdateStatus/${id}`);
    const res = await axios.put<any, AppResponse<any>>(ep, { status });
    return res;
  }
  public static async getBroadcastMessages(userId: string): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/getbroadcastMessage");
    const res = await axios.post<any, AppResponse<any>>(ep, { userId });
    return res;
  }

  public static async getBroadcastCustomerList(): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/broadcast/list");
    const res = await axios.get<any, AppResponse<any>>(ep);
    return res;
  }

  public static async createPromoCode(data?: any): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/promoCodeCreate");
    const res = await axios.post<any, AppResponse<any>>(ep, data);
    return res;
  }

  public static async deletePromoCode(id: string): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/PromoCodeDelete/${id}`);
    const res = await axios.delete<any, AppResponse<any>>(ep);
    return res;
  }

  public static async updatePromoCodeStatus(id: string, status: "Active" | "Inactive"): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/promoCodeUpdateStatus/${id}`);
    const res = await axios.put<any, AppResponse<any>>(ep, { status });
    return res;
  }
  public static async updatePromoLimits(id: string, payload: { totalQuantity?: number; perAccountLimit?: number; }): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/updatePromoLimits/${id}`);
    const res = await axios.put<any, AppResponse<any>>(ep, payload);
    return res;
  }
  public static async togglePromotions(enabled: boolean): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/promos/settings"); // matches backend endpoint
    const res = await axios.put<any, AppResponse<any>>(ep, { enabled });
    return res;
  }
  public static async getPromoStatus(): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/getPromoStatus");
    const res = await axios.get<any, AppResponse<any>>(ep);
    return res;
  }

  public static async getPromoCode(userId: string): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/getPromoCode");
    const res = await axios.post<any, AppResponse<any>>(ep, { userId });
    return res;
  }
  public static async getbulkRequest(data: any): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/getbulkRequest`);
    const res = await axios.get<any, AppResponse<any>>(ep, { data });
    return res;
  }

  public static async getCompletedbulkRequest(): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/getbulkRequest/completed`);
    const res = await axios.get<any, AppResponse<any>>(ep);
    return res;
  }
  public static async getUnCompletedbulkRequest(): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl(`admin/getbulkRequest/uncompleted`);
    const res = await axios.get<any, AppResponse<any>>(ep);
    return res;
  }

  public static async approvebulkRequest(bulkRequestId: string): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/approveBulkRequest");
    const res = await axios.post<any, AppResponse<any>>(ep, { bulkRequestId });
    return res;
  }
  public static async processbulkRequest(bulkRequestId: string): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/processBulkRequest");
    const res = await axios.post<any, AppResponse<any>>(ep, { bulkRequestId });
    return res;
  }

  public static async uploadBulkFile(pickupData: any, deliveries: any[], pickupDateTime: string, deliveryDateTime: string, settingsType: string, customerId?: string, bulkRequestId?: string,): Promise<{
    bulkRequestId: string; orderIds: string[]; total: number; prices: number[], totalDistance: number[];
  }> {
    const ep = Util.apiAuthUrl("admin/bulkRequestFileUpload");
    const payload = {
      pickup: pickupData, deliveries, pickupDateTime, deliveryDateTime, bulkRequestId, customerId, settingsType
    };
    const { data } = await axios.post(ep, payload);
    return data;
  }

  public static async updateDriverActiveDeactive(driver: User): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("admin/user/active-deactive");
    const res = await axios.post<User, AppResponse<string>>(ep, driver);
    return res;
  }

  // public static async updateDeliveryPrice(data: any): Promise<AppResponse<any>> {
  //   const ep = Util.apiAuthUrl("admin/updateBulkPrice");
  //   const res = await axios.post<any, AppResponse<any>>(ep, { 
  //     deliveryId: data.deliveryId, 
  //     price: data.price 
  //   });
  //   return res;
  // }
  public static async updateDeliveryPrice(data: {
    deliveryIds: string[];   // one or many
    price: number;           // same price for all
  }): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/updateBulkPrice");
    const payload = {
      deliveryIds: Array.isArray(data.deliveryIds) ? data.deliveryIds : [data.deliveryIds],
      price: data.price
    };
    const res = await axios.post<any, AppResponse<any>>(ep, payload);
    return res;
  }
  ////////////-----------start Shift-------------------------/////


  public static async createShift(data: ShiftRequest): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/shift/create");
    const res = await axios.post<Shift, AppResponse<any>>(ep, data);
    return res;
  }


  public static async editShift(data: ShiftRequest): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/shift/edit");
    const res = await axios.post<Shift, AppResponse<any>>(ep, data);
    return res;
  }


  public static async deleteShift(data: { _id: string }): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("admin/shift/delete");
    const res = await axios.post<{ _id: string }, AppResponse<any>>(ep, data);
    return res;
  }



  public static async getAllShifts(): Promise<AppResponse<Shift[]>> {
    // const ep = Util.apiAuthUrl(`admin/shift/list/${offset}/${limit}`);
    const ep = Util.apiAuthUrl("admin/shift/list");
    const res = await axios.get<void, AppResponse<Shift[]>>(ep);
    return res;
  }

  ////////////-----------end Shift-------------------------/////

  public static async getDriversWithAvailability(
    limit: number,
    offset: number
  ): Promise<AppResponse<any>> {
    try {
      const ep = Util.apiAuthUrl(`admin/drivers/with-availability/${offset}/${limit}`);
      const res = await axios.get<void, AppResponse<any>>(ep);
      return res;
    } catch (error) {
      console.error("Error fetching drivers with availability:", error);
      throw error;
    }
  }

  /*  Search drivers with availability */
  public static async searchDriversWithAvailability(
    searchData: { searchableString: string },
    limit: number,
    offset: number
  ): Promise<AppResponse<any>> {
    try {
      const ep = Util.apiAuthUrl(`admin/drivers/search-with-availability/${offset}/${limit}`);
      const res = await axios.post<any, AppResponse<any>>(ep, searchData);
      return res;
    } catch (error) {
      console.error("Error searching drivers with availability:", error);
      throw error;
    }
  }

  /**
   * Get availability for specific driver
   */
  public static async getDriverAvailability(driverId: string): Promise<AppResponse<any>> {
    try {
      const ep = Util.apiAuthUrl(`admin/driver/${driverId}/availability`);
      const res = await axios.get<void, AppResponse<any>>(ep);
      return res;
    } catch (error) {
      console.error("Error fetching driver availability:", error);
      throw error;
    }
  }

  /* Update driver availability slot */
  public static async updateDriverAvailabilitySlot(data: {
    driverId: string;
    slotId: string;
    dayOfWeek?: number;
    startTime?: string;
    endTime?: string;
    repeatWeekly?: boolean;
    isAllDay?: boolean;
  }): Promise<AppResponse<any>> {
    try {
      const ep = Util.apiAuthUrl(`admin/driver/availability/update`);
      const res = await axios.post<any, AppResponse<any>>(ep, data);
      return res;
    } catch (error) {
      console.error("Error updating driver availability:", error);
      throw error;
    }
  }

  /* Add new availability slot for driver */
  public static async addDriverAvailabilitySlot(data: {
    driverId: string;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    repeatWeekly: boolean;
    isAllDay: boolean;
  }): Promise<AppResponse<any>> {
    try {
      const ep = Util.apiAuthUrl(`admin/driver/availability/add`);
      const res = await axios.post<any, AppResponse<any>>(ep, data);
      return res;
    } catch (error) {
      console.error("Error adding driver availability:", error);
      throw error;
    }
  }

  /* Delete availability slot */
  public static async deleteDriverAvailabilitySlot(
    driverId: string,
    slotId: string
  ): Promise<AppResponse<any>> {
    try {
      const ep = Util.apiAuthUrl(`admin/driver/${driverId}/availability/${slotId}`);
      const res = await axios.delete<void, AppResponse<any>>(ep);
      return res;
    } catch (error) {
      console.error("Error deleting driver availability:", error);
      throw error;
    }
  }

  public static async generateDriverRoutesPDF(data: DriverRoutesRequest): Promise<AppResponse<ArrayBuffer>> {
    const ep = Util.apiAuthUrl("admin/driver-routes/pdf");
    const res = await axios.post<DriverRoutesRequest, AppResponse<ArrayBuffer>>(ep, data, {
      responseType: "arraybuffer",
    });
    return res;
  }
}
